// HELPER
import {
  cleanSortParam,
  FLOW_GATEWAY,
  initHttpParams,
  setWorkObjectTypeIdPath,
} from '@workheld/workheld-shared-lib';

// ENVIRONMENT
import { environment } from '../../environments/environment';

// ANGULAR
import { FormBuilder, FormGroup } from '@angular/forms';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

// RxJS 6
import { Observable, of } from 'rxjs';

// MODELS
import { WHHttpParams } from '@workheld/workheld-shared-lib';
import {
  IWHChecklistInstancePreviewDTO,
  IWHChecklistInstanceDTO,
} from '@workheld/workheld-shared-lib';

@Injectable()
export class ChecklistInstanceAsyncService {
  constructor(private httpClient: HttpClient) {}

  // CHECKLIST INSTANCE PROCESS
  public initChecklistInstanceProcessFormGroup(): FormGroup {
    return new FormBuilder().group({
      checklistInstanceStatusId: [null],
      managerComment: [null],
    });
  }

  // CHECKLIST INSTANCE PREVIEW
  public initChecklistInstancePreviewHttpParamsFormGroup(): FormGroup {
    return new FormBuilder().group({
      page: [0],
      sort: ['createdDate,DESC'],
      term: [null],
      sortMode: ['DESC'],
    });
  }

  // CHECKLIST INSTANCE PREVIEW
  public initChecklistInstancePrevievPagedHttpParamsFormGroup(): FormGroup {
    return new FormBuilder().group({
      page: [0],
      sort: ['createdDate,DESC'],
      term: [null],
      filter: null,
    });
  }

  // CRUD
  public getChecklistInstanceDtoByChecklistInstanceId(
    checklistInstanceId: string,
  ): Observable<IWHChecklistInstanceDTO> {
    return this.httpClient.get<IWHChecklistInstanceDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/checklistInstances/${checklistInstanceId}`,
    );
  }

  public updateChecklistInstanceByChecklistInstanceID(
    checklistInstanceId: string,
    checklistInstancePayload: any,
  ): Observable<any> {
    return this.httpClient.post<any>(
      `${environment.apiUrl + FLOW_GATEWAY}/checklistInstances/${checklistInstanceId}`,
      checklistInstancePayload,
    );
  }

  // PREVIEW
  public getChecklistInstancePreview(
    params: WHHttpParams,
    targetParent: string,
    targetID: string,
    workObjectTypeId: number,
  ): Observable<IWHChecklistInstancePreviewDTO> {
    const httpParams: HttpParams = initHttpParams(params);
    switch (targetParent) {
      case 'ALL':
        return this.getChecklistInstancePreviewForAllTypes(httpParams);
      case 'EQUIPMENT':
        return this.getChecklistInstancePreviewByForNode(targetID, httpParams);
      case 'WORK_OBJECT':
        return this.getChecklistInstancePreviewByForWorkObject(
          targetID,
          workObjectTypeId,
          httpParams,
        );
      case 'WORK_ORDER':
        return this.getChecklistInstancePreviewByForWorkOrder(
          targetID,
          httpParams,
        );
      case 'BOMNODE':
        return this.getChecklistInstancePreviewByForNode(targetID, httpParams);
      default:
        return of(null);
    }
  }
  private getChecklistInstancePreviewForAllTypes(
    httpParams: HttpParams,
  ): Observable<IWHChecklistInstancePreviewDTO> {
    if (!httpParams.get('filter')) {
      httpParams = httpParams.set('filter', 'ALL');
    }
    return this.httpClient.get<IWHChecklistInstancePreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/checklistInstances/preview`,
      { params: httpParams },
    );
  }

  private getChecklistInstancePreviewByForWorkObject(
    workObjectId: string,
    workObjectTypeId: number,
    httpParams: HttpParams,
  ): Observable<IWHChecklistInstancePreviewDTO> {
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));

    const workObjectTargetEndpoint: string = setWorkObjectTypeIdPath(workObjectTypeId);
    return this.httpClient.get<IWHChecklistInstancePreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/${workObjectTargetEndpoint}/${workObjectId}/checklistInstances/preview`,
      { params: httpParams },
    );
  }

  // WORK ORDER LEVEL
  private getChecklistInstancePreviewByForWorkOrder(
    workOrderId: string,
    httpParams: HttpParams,
  ): Observable<IWHChecklistInstancePreviewDTO> {
    httpParams = httpParams.set('sort', cleanSortParam(httpParams.get('sort')));

    return this.httpClient.get<IWHChecklistInstancePreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/workOrders/${workOrderId}/checklistInstances/preview`,
      { params: httpParams },
    );
  }

  // NODE LEVEL
  private getChecklistInstancePreviewByForNode(
    nodeId: string,
    httpParams: HttpParams,
  ): Observable<IWHChecklistInstancePreviewDTO> {
    return this.httpClient.get<IWHChecklistInstancePreviewDTO>(
      `${environment.apiUrl + FLOW_GATEWAY}/bomnode/${nodeId}/checklistInstances/preview`,
      { params: httpParams },
    );
  }
}
